// 2026 input design. Figma "Input fields styleguide update", component set 12720:69721.
//
// `inputChrome` is the shared border, background and state styling, and it deliberately lives OUTSIDE `baseStyle`.
// Textarea.ts and Select.ts spread `Input.baseStyle.field` by value, and two controls render off `baseStyle` alone —
// ProductQuantitySelector.tsx:50 (`variant="unstyled"`) and ProductCardQuantitySelector.tsx:56 (`variant="transparent"`,
// a name no variant defines). Ticket #4366 leaves both of those alone, so chrome in `baseStyle` would leak into
// controls that must not move. Keeping it in the variants prevents that structurally rather than patching it back out
// with per-call-site overrides. Geometry is not shared either, because a textarea must not inherit the fixed 48px box.
export const inputChrome = {
  bg: "primary.white",
  border: "1px solid",
  borderColor: "border.lightest",
  borderRadius: "8px",
  _placeholder: {
    color: "text.placeholder",
  },
  // Design draws hover pixel-identical to default. Pinned rather than omitted so Chakra's own hover cannot show through.
  _hover: {
    borderColor: "border.lightest",
  },
  // Chakra has no warning state. Opt in with `data-warning` on the field, and pair it with a `data-warning` helper text.
  "&[data-warning]": {
    borderColor: "border.mid",
  },
  // Design removes the border on error and replaces it with a 1px ring. One red for both ring and helper text
  // (`system.error`); the design file carries a second, near-identical red on the ring only.
  _invalid: {
    borderColor: "transparent",
    boxShadow: "0 0 0 1px var(--chakra-colors-system-error)",
  },
  // Declared after `_invalid` on purpose: equal specificity, so source order decides, and focus should win on an
  // invalid field the user is actively editing.
  _focus: {
    borderColor: "border.dark",
    boxShadow: "none",
  },
  // Chakra's default `_disabled` is `opacity: 0.4`, which washes out the specified colours. Pinned to 1.
  _disabled: {
    bg: "grey.lightest",
    color: "text.disabled",
    opacity: 1,
  },
}

export const Input = {
  parts: ["field"],
  // Typography only. Everything here also reaches Textarea, Select and both quantity steppers.
  baseStyle: {
    field: {
      fontFamily: "larsseit",
      fontFeatureSettings: '"ss01" on, "ss02" on',
      fontWeight: "normal",
      fontSize: "0.875rem",
      lineHeight: 1.4,
      letterSpacing: "-0.02em",
      color: "text.primary",
      _disabled: {
        cursor: "default",
        pointerEvents: "none",
      },
    },
  },
  sizes: {
    default: {},
  },
  variants: {
    primary: {
      field: {
        ...inputChrome,
        h: "48px",
        px: "12px",
        py: "8px",
      },
    },
    secondary: {
      field: {
        borderRadius: "40px",
        px: 6,
        py: 1,
      },
    },
    // Special variants for specific <Input> Component
    search: {
      field: {
        fontSize: { base: "1.875rem", sm: "3.75rem" },
        textAlign: "center",
        lineHeight: { base: 1.4, sm: 0.7 },
        letterSpacing: "normal",
        color: "primary.midGrey",
        bgColor: "primary.white",
        border: "none",
        borderBottom: "1px solid",
        borderColor: "primary.midGrey",
        borderRadius: 0,
        py: 12.5,
        _placeholder: {
          color: "text.placeholder",
        },
        _hover: {
          borderColor: "primary.midGrey",
        },
        _active: {
          borderColor: "primary.midGrey",
        },
        // Design draws no focus state for this variant, so the browser outline is suppressed and the border stays put.
        _focus: {
          outline: "none",
          borderColor: "primary.midGrey",
        },
      },
    },
    // Footer email. Named in the ticket and specced in Figma (12720:70565): 8px radius and a 64x48 trailing submit
    // button. `pr` reserves the button's width; the button itself lives in SubscribeForm.tsx.
    //
    // Type size is the shared 14px from `baseStyle`, not the 16px the Figma frame draws: the field carries a floating
    // label, whose `pt: 20px` / `pb: 8px` leaves ~20px of line box inside the fixed 48px height. 16px text overflows it.
    subscribe: {
      field: {
        ...inputChrome,
        h: "48px",
        px: "12px",
        py: "8px",
        pr: "64px",
        // Focus is a ring on the FormControl (SubscribeForm.tsx) so it can enclose the submit button; the border stays put.
        _focus: {
          borderColor: "border.lightest",
          boxShadow: "none",
        },
      },
    },
  },
  // LOAD-BEARING. `defaultProps: {}` deep-merges under Chakra's own `{ size: "md", variant: "outline" }`, so a bare
  // <Input> silently renders Chakra stock — a 40px box with a blue focus shadow — instead of the design.
  // `size: "default"` is an empty recipe that neutralises Chakra's `sizes.md`.
  defaultProps: {
    size: "default",
    variant: "primary",
  },
}
